// Spelling Bee content. Five bands, 30+ words each, every word with a kid sentence that uses
// it exactly once (the game blanks the word out of the sentence for silent play).

export interface SpellWord { word: string; sentence: string }

export const SPELLING: Record<1 | 2 | 3 | 4 | 5, SpellWord[]> = {
  // band 1: rungs 1-2, CVC and blends
  1: [
    { word: 'ship', sentence: 'The ship sailed across the bay.' },
    { word: 'frog', sentence: 'A frog hopped onto the lily pad.' },
    { word: 'drum', sentence: 'She hit the drum with two sticks.' },
    { word: 'clap', sentence: 'We clap when the song ends.' },
    { word: 'nest', sentence: 'The bird built a nest in the oak tree.' },
    { word: 'jump', sentence: 'Can you jump over the puddle?' },
    { word: 'milk', sentence: 'He poured milk on his cereal.' },
    { word: 'hand', sentence: 'Raise your hand if you know the answer.' },
    { word: 'fish', sentence: 'A tiny fish swam in the pond.' },
    { word: 'crab', sentence: 'The crab scuttled sideways across the sand.' },
    { word: 'swim', sentence: 'We swim at the pool on hot days.' },
    { word: 'plant', sentence: 'Water the plant so it can grow.' },
    { word: 'stop', sentence: 'The bus will stop at the corner.' },
    { word: 'bell', sentence: 'The bell rang at the end of class.' },
    { word: 'lamp', sentence: 'Turn on the lamp so we can read.' },
    { word: 'sock', sentence: 'One sock is missing from the dryer.' },
    { word: 'trip', sentence: 'We took a trip to the beach.' },
    { word: 'snap', sentence: 'The twig made a snap when it broke.' },
    { word: 'grin', sentence: 'He had a big grin on his face.' },
    { word: 'wind', sentence: 'The wind blew the leaves around.' },
    { word: 'desk', sentence: 'Her desk is covered with crayons.' },
    { word: 'flag', sentence: 'The flag waved on top of the pole.' },
    { word: 'spot', sentence: 'The puppy has a black spot on its ear.' },
    { word: 'tent', sentence: 'We slept in a tent under the stars.' },
    { word: 'glad', sentence: 'I am glad you came to play.' },
    { word: 'plum', sentence: 'The plum was sweet and purple.' },
    { word: 'sled', sentence: 'The sled flew down the snowy hill.' },
    { word: 'brick', sentence: 'The wall is made of red brick.' },
    { word: 'duck', sentence: 'A duck paddled across the lake.' },
    { word: 'pink', sentence: 'Her favorite color is pink.' },
    { word: 'slip', sentence: 'Be careful not to slip on the ice.' },
    { word: 'bump', sentence: 'There is a bump in the road.' },
  ],
  // band 2: rungs 3-4, vowel teams and silent e
  2: [
    { word: 'train', sentence: 'The train chugged down the track.' },
    { word: 'smile', sentence: 'Her smile lit up the room.' },
    { word: 'boat', sentence: 'The boat rocked gently on the waves.' },
    { word: 'rain', sentence: 'The rain tapped on the window all night.' },
    { word: 'cake', sentence: 'We baked a cake for Grandma.' },
    { word: 'sleep', sentence: 'Bears sleep through most of the winter.' },
    { word: 'moon', sentence: 'The moon was full and bright.' },
    { word: 'stone', sentence: 'She skipped a stone across the pond.' },
    { word: 'green', sentence: 'The grass turned green in spring.' },
    { word: 'kite', sentence: 'The kite danced high in the wind.' },
    { word: 'road', sentence: 'The road winds through the hills.' },
    { word: 'leaf', sentence: 'One red leaf fell from the tree.' },
    { word: 'bone', sentence: 'The dog buried a bone in the yard.' },
    { word: 'mail', sentence: 'The mail comes at noon.' },
    { word: 'coat', sentence: 'Wear your coat, it is cold out.' },
    { word: 'teeth', sentence: 'Brush your teeth before bed.' },
    { word: 'whale', sentence: 'A whale is the biggest animal in the sea.' },
    { word: 'snail', sentence: 'The snail left a shiny trail.' },
    { word: 'spoon', sentence: 'Stir the soup with a spoon.' },
    { word: 'float', sentence: 'Corks float on water.' },
    { word: 'bike', sentence: 'I ride my bike to school.' },
    { word: 'seed', sentence: 'A tiny seed can grow into a tree.' },
    { word: 'toad', sentence: 'The toad sat very still in the mud.' },
    { word: 'flame', sentence: 'The candle flame flickered.' },
    { word: 'beach', sentence: 'We built a castle at the beach.' },
    { word: 'cube', sentence: 'An ice cube melted in my hand.' },
    { word: 'paint', sentence: 'We used blue paint for the sky.' },
    { word: 'wheel', sentence: 'The wheel on the cart squeaked.' },
    { word: 'globe', sentence: 'Spin the globe and find Florida.' },
    { word: 'cloud', sentence: 'That cloud looks like a rabbit.' },
    { word: 'sweet', sentence: 'Honey is very sweet.' },
    { word: 'bread', sentence: 'Fresh bread smells wonderful.' },
  ],
  // band 3: rungs 5-6, multisyllable and r-controlled
  3: [
    { word: 'garden', sentence: 'Tomatoes grow in our garden.' },
    { word: 'thunder', sentence: 'The thunder rumbled after the lightning.' },
    { word: 'winter', sentence: 'In winter the pond freezes over.' },
    { word: 'rocket', sentence: 'The rocket roared into the sky.' },
    { word: 'turtle', sentence: 'A turtle carries its home on its back.' },
    { word: 'circle', sentence: 'Draw a circle around the right answer.' },
    { word: 'farmer', sentence: 'The farmer fed the chickens at dawn.' },
    { word: 'purple', sentence: 'Grapes can be purple or green.' },
    { word: 'morning', sentence: 'Birds sing early in the morning.' },
    { word: 'basket', sentence: 'The basket was full of apples.' },
    { word: 'spider', sentence: 'A spider spun a web in the corner.' },
    { word: 'number', sentence: 'Pick a number between one and ten.' },
    { word: 'butter', sentence: 'Spread butter on the warm toast.' },
    { word: 'corner', sentence: 'The cat sleeps in the corner of the room.' },
    { word: 'dinner', sentence: 'We had pasta for dinner.' },
    { word: 'forest', sentence: 'Owls live deep in the forest.' },
    { word: 'hammer', sentence: 'Dad used a hammer to fix the shelf.' },
    { word: 'market', sentence: 'We bought peaches at the market.' },
    { word: 'planet', sentence: 'Mars is a red planet.' },
    { word: 'pocket', sentence: 'I keep a shell in my pocket.' },
    { word: 'river', sentence: 'The river flows to the sea.' },
    { word: 'summer', sentence: 'In summer the days are long.' },
    { word: 'window', sentence: 'Sunlight came through the window.' },
    { word: 'yellow', sentence: 'The school bus is yellow.' },
    { word: 'carpet', sentence: 'The carpet is soft under my feet.' },
    { word: 'silver', sentence: 'Her necklace is made of silver.' },
    { word: 'monster', sentence: 'The monster in the story was friendly.' },
    { word: 'perfect', sentence: 'The weather was perfect for a picnic.' },
    { word: 'blanket', sentence: 'She wrapped up in a warm blanket.' },
    { word: 'ladder', sentence: 'He climbed the ladder to the roof.' },
    { word: 'pepper', sentence: 'A little pepper makes the soup spicy.' },
    { word: 'cartoon', sentence: 'My favorite cartoon is on Saturday.' },
  ],
  // band 4: rungs 7-8, prefixes and suffixes
  4: [
    { word: 'unhappy', sentence: 'The lost puppy looked unhappy.' },
    { word: 'careful', sentence: 'Be careful crossing the street.' },
    { word: 'wonderful', sentence: 'We had a wonderful day at the zoo.' },
    { word: 'replay', sentence: 'Tap replay to hear the song again.' },
    { word: 'teacher', sentence: 'Our teacher reads to us after lunch.' },
    { word: 'helpful', sentence: 'A map is helpful when you are lost.' },
    { word: 'kindness', sentence: 'Her kindness made everyone smile.' },
    { word: 'quickly', sentence: 'The rabbit ran quickly into the bushes.' },
    { word: 'unlock', sentence: 'Use the key to unlock the gate.' },
    { word: 'preview', sentence: 'We watched a preview of the movie.' },
    { word: 'disagree', sentence: 'Friends can disagree and still be friends.' },
    { word: 'hopeless', sentence: 'The tangled kite string looked hopeless.' },
    { word: 'darkness', sentence: 'Fireflies glow in the darkness.' },
    { word: 'rebuild', sentence: 'The ants will rebuild their hill.' },
    { word: 'fearless', sentence: 'The fearless kitten chased the dog.' },
    { word: 'slowly', sentence: 'The turtle moved slowly across the path.' },
    { word: 'painter', sentence: 'The painter mixed red and blue.' },
    { word: 'misplace', sentence: 'I always misplace my left shoe.' },
    { word: 'powerful', sentence: 'The storm brought powerful winds.' },
    { word: 'thankful', sentence: 'I am thankful for my family.' },
    { word: 'unfair', sentence: 'Skipping my turn is unfair.' },
    { word: 'playful', sentence: 'The playful otters splashed each other.' },
    { word: 'sadness', sentence: 'The sad song was full of sadness.' },
    { word: 'untie', sentence: 'Help me untie this knot.' },
    { word: 'colorful', sentence: 'The parrot has colorful feathers.' },
    { word: 'reread', sentence: 'I like to reread my favorite book.' },
    { word: 'softly', sentence: 'She hummed softly to the baby.' },
    { word: 'speechless', sentence: 'The surprise left him speechless.' },
    { word: 'invisible', sentence: 'The cape made her invisible in the game.' },
    { word: 'illness', sentence: 'Rest helps you recover from an illness.' },
    { word: 'nonstop', sentence: 'The puppy barked nonstop at the mail truck.' },
    { word: 'brightly', sentence: 'The stars shone brightly over the camp.' },
  ],
  // band 5: rungs 9-10, tricky spellings
  5: [
    { word: 'necessary', sentence: 'Water is necessary for every living thing.' },
    { word: 'rhythm', sentence: 'Clap along to the rhythm of the drum.' },
    { word: 'separate', sentence: 'Please separate the red blocks from the blue.' },
    { word: 'beautiful', sentence: 'The sunset was beautiful tonight.' },
    { word: 'because', sentence: 'We stayed inside because it was raining.' },
    { word: 'friend', sentence: 'My friend saved me a seat.' },
    { word: 'island', sentence: 'The island had a sandy beach all around.' },
    { word: 'knight', sentence: 'The knight wore shining armor.' },
    { word: 'science', sentence: 'In science we grew crystals.' },
    { word: 'different', sentence: 'Every snowflake is different.' },
    { word: 'surprise', sentence: 'The party was a complete surprise.' },
    { word: 'calendar', sentence: 'Mark the date on the calendar.' },
    { word: 'favorite', sentence: 'Blue is my favorite color.' },
    { word: 'library', sentence: 'The library is quiet and full of books.' },
    { word: 'knowledge', sentence: 'Reading fills your head with knowledge.' },
    { word: 'piece', sentence: 'May I have a piece of cake?' },
    { word: 'weird', sentence: 'That noise in the attic was weird.' },
    { word: 'receive', sentence: 'Did you receive my letter?' },
    { word: 'mystery', sentence: 'The missing sock is a mystery.' },
    { word: 'strength', sentence: 'An ant has amazing strength for its size.' },
    { word: 'answer', sentence: 'Raise your hand to give the answer.' },
    { word: 'tongue', sentence: 'A chameleon catches bugs with its tongue.' },
    { word: 'stomach', sentence: 'My stomach growled before lunch.' },
    { word: 'chocolate', sentence: 'Hot chocolate is best on a cold day.' },
    { word: 'measure', sentence: 'Use a ruler to measure the line.' },
    { word: 'oxygen', sentence: 'Plants make the oxygen we breathe.' },
    { word: 'exercise', sentence: 'Jumping rope is good exercise.' },
    { word: 'skeleton', sentence: 'Your skeleton has more than two hundred bones.' },
    { word: 'guard', sentence: 'The dog will guard the yard.' },
    { word: 'vegetable', sentence: 'Broccoli is my least favorite vegetable.' },
    { word: 'yacht', sentence: 'A yacht is a fancy boat.' },
    { word: 'biscuit', sentence: 'She dunked a biscuit in her tea.' },
  ],
};

export function spellingBandFor(level: number): 1 | 2 | 3 | 4 | 5 {
  if (level <= 2) return 1;
  if (level <= 4) return 2;
  if (level <= 6) return 3;
  if (level <= 8) return 4;
  return 5;
}

/** the sentence with the target word replaced by a blank (case-insensitive, whole word) */
export function blankOut(sw: SpellWord): string {
  const re = new RegExp(`\\b${sw.word}\\b`, 'i');
  return sw.sentence.replace(re, '___');
}
